// Links to the per-city listing pages (boat rental, wine tours, a city's tours
// and events) and their region-wide hubs, labelled with each page's own title —
// the phrase it's meant to rank for. Shared by the article pages' "Plan your
// trip" links and the "tours & boats in this city" block (CityCrossSell).
// Callers pass which cities have each page exported (the footer's baked
// lists), so nothing here can 404.

import type { Href } from 'expo-router';

import { cityEventsCopy, cityToursCopy } from '@/lib/i18n/city-listings-content';
import type { Locale } from '@todo/core/i18n/locales';
import type { FooterCityLinks } from '@todo/core/list-queries';

type Translate = (key: string) => string;

export type ListingLink = { key: string; href: Href; label: string };

export type ListingPageKind = keyof FooterCityLinks;

export function cityPageLink(
  kind: ListingPageKind,
  cityId: string,
  locale: Locale,
  t: Translate,
): ListingLink {
  switch (kind) {
    case 'boatRental':
      return {
        key: `boat-rental:${cityId}`,
        href: { pathname: '/[lang]/boat-rental/[city]', params: { lang: locale, city: cityId } },
        label: t(`boatRental.cities.${cityId}.title`),
      };
    case 'wineTours':
      return {
        key: `wine-tours:${cityId}`,
        href: { pathname: '/[lang]/wine-tours/[city]', params: { lang: locale, city: cityId } },
        label: t(`wineTours.cities.${cityId}.title`),
      };
    case 'cityTours':
      return {
        key: `tours:${cityId}`,
        href: { pathname: '/[lang]/[city]/tours', params: { lang: locale, city: cityId } },
        label: cityToursCopy(locale, cityId)?.title ?? cityId,
      };
    case 'cityEvents':
      return {
        key: `events:${cityId}`,
        href: { pathname: '/[lang]/[city]/events', params: { lang: locale, city: cityId } },
        label: cityEventsCopy(locale, cityId)?.title ?? cityId,
      };
  }
}

/** The region-wide hub, for a city without an exported page of that kind (or no city at all). */
export function hubLink(kind: ListingPageKind, locale: Locale, t: Translate): ListingLink {
  switch (kind) {
    case 'boatRental':
      return { key: 'hub:boats', href: { pathname: '/[lang]/boats', params: { lang: locale } }, label: t('nav.boats') };
    case 'wineTours':
      return {
        key: 'hub:wine-tours',
        href: { pathname: '/[lang]/wine-tours', params: { lang: locale } },
        label: t('wineTours.region.title'),
      };
    case 'cityTours':
      return { key: 'hub:tours', href: { pathname: '/[lang]/tours', params: { lang: locale } }, label: t('nav.tours') };
    case 'cityEvents':
      return { key: 'hub:events', href: { pathname: '/[lang]/events', params: { lang: locale } }, label: t('nav.events') };
  }
}

/** The city's page of that kind when it was exported, else the hub. */
export function cityOrHubLink(
  kind: ListingPageKind,
  cityId: string | null,
  pages: FooterCityLinks,
  locale: Locale,
  t: Translate,
): ListingLink {
  return cityId && pages[kind].includes(cityId)
    ? cityPageLink(kind, cityId, locale, t)
    : hubLink(kind, locale, t);
}

// What the "tours & boats in this city" block links on to: the catalogues that
// earn (tours, boat rental, wine tours) — never events, which is what the
// pages carrying the block already are or link to.
const CROSS_SELL_KINDS: ListingPageKind[] = ['cityTours', 'boatRental', 'wineTours'];

export function crossSellLinks(
  cityId: string | null,
  pages: FooterCityLinks,
  locale: Locale,
  t: Translate,
): ListingLink[] {
  return CROSS_SELL_KINDS.map((kind) => cityOrHubLink(kind, cityId, pages, locale, t));
}
